import { type ReactNode } from 'react';

import {
  ColumnChart,
  DonutChart,
  type DonutSegment,
  HeatGrid,
  LegendRow,
  LineChart,
  seriesColor,
  StackedBar,
} from '@backrs_ui/common-ui';

import { type ChartSpec, type ChartType } from '@backrs_ui/grow-ui/src/models/models';

import { Box, type SxProps, type Theme, Typography } from '@mui/material';

const STACKED_BAR_HEIGHT = 28;

// Where LineChart's plot area starts and ends under `showValues`, as a share of
// its viewBox, so the category labels line up with the points.
const LINE_INSET_LEFT = '7.8%';
const LINE_INSET_RIGHT = '2.5%';

const DONUT_SIZE = 160;
const COLUMN_TICKS = 5;

// Counts get a whole-number axis: the ceiling is the next multiple of the tick
// count above the tallest bar. Fractional data keeps ColumnChart's own scale.
const countCeiling = (values: readonly number[]): number | undefined => {
  if (!values.every(Number.isInteger)) return undefined;

  const peak = Math.max(...values, 0);

  return Math.ceil((peak + 1) / COLUMN_TICKS) * COLUMN_TICKS;
};

/** Whole-number shares that add up to exactly 100, so the donut closes. */
const wholeShares = (values: readonly number[]): number[] => {
  const total = values.reduce((sum, value) => sum + value, 0);

  if (total <= 0) return values.map(() => 0);

  const exact = values.map((value) => (value / total) * 100);
  const shares = exact.map(Math.floor);
  const shortfall = 100 - shares.reduce((sum, share) => sum + share, 0);

  exact
    .map((value, index) => ({ index, fraction: value - shares[index] }))
    .sort((a, b) => b.fraction - a.fraction)
    .slice(0, shortfall)
    .forEach(({ index }) => {
      shares[index] += 1;
    });

  return shares;
};

// One renderer per chart type; a new type needs an entry here and a value in CHART_TYPES.
const CHART_RENDERERS: Readonly<Record<ChartType, (spec: ChartSpec) => ReactNode>> = {
  column: (spec) => {
    const values = spec.series[0]?.values ?? [];

    return (
      <ColumnChart
        bars={spec.categories.map((label, index) => ({
          id: String(index),
          label,
          value: values[index] ?? 0,
        }))}
        maxValue={countCeiling(values)}
        color="Brand/500"
        ariaLabel={spec.title}
        tickCount={COLUMN_TICKS}
      />
    );
  },
  line: (spec) => (
    <>
      {spec.series.length > 1 && (
        <Box sx={rowLegendSx}>
          {spec.series.map((series, index) => (
            <LegendRow key={series.name} color={seriesColor(index).fill} label={series.name} />
          ))}
        </Box>
      )}
      <LineChart
        series={spec.series.map((series, index) => ({
          id: series.name,
          points: series.values,
          color: seriesColor(index).fill,
        }))}
        ariaLabel={spec.title}
        showValues
      />
      <Box sx={lineAxisSx}>
        {spec.categories.map((label, index) => (
          <Typography key={index} variant="text-xs-medium" sx={axisTickSx}>
            {label}
          </Typography>
        ))}
      </Box>
    </>
  ),
  donut: (spec) => {
    const shares = wholeShares(spec.series[0]?.values ?? []);
    const segments: DonutSegment[] = spec.categories.map((label, index) => ({
      label,
      value: shares[index],
      color: seriesColor(index).fill,
    }));

    return (
      <Box sx={donutRowSx}>
        <DonutChart variant={{ type: 'multi', segments }} size={DONUT_SIZE} />
        <Box sx={columnLegendSx}>
          {segments.map((segment) => (
            <LegendRow
              key={segment.label}
              color={segment.color}
              label={segment.label}
              pct={segment.value}
            />
          ))}
        </Box>
      </Box>
    );
  },
  stacked_bar: (spec) => (
    <>
      <Box sx={rowLegendSx}>
        {spec.series.map((series, index) => (
          <LegendRow key={series.name} color={seriesColor(index).fill} label={series.name} />
        ))}
      </Box>
      {spec.categories.map((label, column) => {
        const shares = wholeShares(spec.series.map((series) => series.values[column] ?? 0));

        return (
          <Box key={column} sx={stackedRowSx}>
            <Typography variant="text-sm-medium" sx={stackedRowLabelSx}>
              {label}
            </Typography>
            <Box sx={stackedBarWrapSx}>
              <StackedBar
                segments={spec.series.map((series, index) => ({
                  key: series.name,
                  pct: shares[index],
                  color: seriesColor(index).fill,
                  labelColor: seriesColor(index).label,
                }))}
                height={STACKED_BAR_HEIGHT}
              />
            </Box>
          </Box>
        );
      })}
    </>
  ),
  heat_grid: (spec) => (
    <HeatGrid
      rows={spec.series.map((series, index) => ({
        id: String(index),
        label: series.name,
        counts: Object.fromEntries(series.values.map((value, column) => [String(column), value])),
      }))}
      columns={spec.categories.map((label, index) => ({ id: String(index), label }))}
      xAxisLabel={spec.x_label ?? ''}
      yAxisLabel={spec.y_label ?? ''}
    />
  ),
};

// Types whose plot has bare axes get the axis labels as captions. The heat grid
// labels its own axes; donut and stacked bar have none.
const CAPTIONED_AXES: ReadonlySet<ChartType> = new Set<ChartType>(['column', 'line']);

const ChatChart = ({ spec }: Readonly<{ spec: ChartSpec }>) => {
  const isCaptioned = CAPTIONED_AXES.has(spec.type);

  return (
    <Box sx={cardSx}>
      <Typography variant="text-md-semibold">{spec.title}</Typography>
      {isCaptioned && spec.y_label ? (
        <Typography variant="text-xs-medium" sx={mutedSx}>
          {spec.y_label}
        </Typography>
      ) : null}
      {CHART_RENDERERS[spec.type](spec)}
      {isCaptioned && spec.x_label ? (
        <Typography variant="text-xs-medium" sx={xLabelSx}>
          {spec.x_label}
        </Typography>
      ) : null}
    </Box>
  );
};

export const MessageCharts = ({ charts }: Readonly<{ charts: readonly ChartSpec[] }>) => (
  <Box sx={listSx}>
    {charts.map((spec, index) => (
      <ChatChart key={`${index}-${spec.title}`} spec={spec} />
    ))}
  </Box>
);

// A fixed width: the bubble shrinks to fit its content, so a percentage would collapse.
const CHART_WIDTH = 560;

const listSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  width: CHART_WIDTH,
  maxWidth: '100%',
  marginBottom: backrs.spacing['spacing-md'],
});

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  padding: backrs.spacing['spacing-xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  boxShadow: backrs.shadows['shadow-xs'],
  boxSizing: 'border-box',
});

const mutedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const xLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  textAlign: 'center',
});

const lineAxisSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  paddingLeft: LINE_INSET_LEFT,
  paddingRight: LINE_INSET_RIGHT,
  boxSizing: 'border-box',
};

const axisTickSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const donutRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-2xl'],
});

const stackedRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const stackedRowLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  flex: '0 0 124px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const stackedBarWrapSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

const rowLegendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-lg'],
});

const columnLegendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});
